/**
 * Helper único para mostrar ticket_number en impresión.
 *
 * Estructura del ticket_number generada por el backend
 * (backend/app/services/ticket_service.py):
 *   {local_num(2)}{DD(2)}{MM(2)}{YY(2)}{seq(>=1)}
 *
 * Ejemplo: "39230426133" → 39 | 23 | 04 | 26 | 133
 *
 * Para ahorrar espacio en impresión térmica, se omite SOLO el segmento del
 * año (posiciones 6-7). El identificador interno (BD, FKs, búsquedas, premios)
 * NO se modifica — solo cambia la representación visual.
 *
 * Reglas:
 *  - Sólo aplica si el código tiene >= 9 caracteres y son todos dígitos
 *    (formato generado por el backend).
 *  - Para códigos legacy / no numéricos (ej. "TK-001", UUIDs) se devuelve
 *    el valor original sin cambios.
 *
 * El ticket corto elimina YY del formato {local}{DD}{MM}{YY}{seq}.
 * Por eso la búsqueda por corto está limitada a 3 meses.
 * No ampliar esta ventana sin cambiar el formato corto: hay colisión anual.
 *
 * Contrato del ticket corto: {local(2)}{DD(2)}{MM(2)}{seq(N≥1)}.
 * `seq` es de longitud variable; NUNCA asumir longitud total fija.
 * Rango aceptado: 7 a 15 caracteres. NO endurecer a longitud fija: en locales
 * de alto volumen el seq diario puede llegar a 4-5 dígitos sin previo aviso.
 * El parser debe leer por posición desde la izquierda y dejar el resto como seq.
 * La UI (Recientes, impresión, listas) y el resolver del backend deben usar
 * EXACTAMENTE este mismo helper. Si divergen, los tickets visibles dejan de
 * poder jalarse o anularse (bug reportado abril 2026).
 */
export function formatTicketForPrint(ticketNumber: string | null | undefined): string {
  const code = (ticketNumber ?? '').trim();
  if (!code) return '';

  // Debe ser todo dígitos y tener al menos local(2)+DD(2)+MM(2)+YY(2)+seq(>=1) = 9 chars
  if (code.length < 9 || !/^\d+$/.test(code)) {
    return code;
  }

  const localPart = code.slice(0, 2);
  const dayPart = code.slice(2, 4);
  const monthPart = code.slice(4, 6);
  // posiciones 6-7 = YY → se omite
  const seqPart = code.slice(8);

  return `${localPart}${dayPart}${monthPart}${seqPart}`;
}
